import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Play, Pause, ArrowsClockwise, Trash, CircleNotch, Cookie, Copy } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
    obterBot, iniciarBot, pararBot, reiniciarBot, removerBot, construirUrlWS, clonarBot,
} from "../lib/api";
import AbaNavegador from "../componentes/AbaNavegador";
import DialogConfirmacao from "../componentes/DialogConfirmacao";
import DialogInjetarCookies from "../componentes/DialogInjetarCookies";
import BotaoSair from "../componentes/BotaoSair";
import AbaRequisicoes from "../componentes/AbaRequisicoes";
import AbaCookies from "../componentes/AbaCookies";
import AbaRegras from "../componentes/AbaRegras";
import AbaLogs from "../componentes/AbaLogs";
import AbaConfig from "../componentes/AbaConfig";

const corStatus = {
    rodando: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    parado: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
    pausado: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
    erro: "bg-red-500/10 text-red-400 border-red-500/30",
    iniciando: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
};

// Intervalo de flush para eventos não-frame (req/log/regra).
// Evita centenas de setState seguidos quando o reCAPTCHA dispara rajadas.
const INTERVALO_FLUSH_MS = 250;

export default function PaginaBot() {
    const { id } = useParams();
    const [bot, setBot] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [aba, setAba] = useState("navegador");

    // Frame: usamos REF para o conteúdo bruto + um "tick" mínimo de estado
    // apenas para forçar render quando há um frame novo. AbaNavegador lê via
    // um getter passado como prop para evitar reconciliar a árvore inteira a cada frame.
    const frameRef = useRef(null); // { imagem, largura, altura }
    const [frameTick, setFrameTick] = useState(0); // incrementa via rAF

    const [requisicoes, setRequisicoes] = useState([]);
    const [logs, setLogs] = useState([]);
    const [eventosRegra, setEventosRegra] = useState([]);

    // Buffers que acumulam entre flushes (evita re-render por cada mensagem WS)
    const buffersRef = useRef({
        novasReq: [],
        novosLogs: [],
        novosEventosRegra: [],
        frameDirty: false,
        statusPendente: null,
    });

    const wsRef = useRef(null);
    const [conectadoWS, setConectadoWS] = useState(false);
    // Ref para AbaRegras: AbaRequisicoes chama abrirComRequisicao() via essa ref
    const abaRegrasRef = useRef(null);
    const abrirRegraDeRequisicao = useCallback((req) => {
        setAba("regras");
        // Aguarda um tick para a aba "regras" montar e ter a ref pronta
        setTimeout(() => {
            if (abaRegrasRef.current && abaRegrasRef.current.abrirComRequisicao) {
                abaRegrasRef.current.abrirComRequisicao(req);
            }
        }, 50);
    }, []);

    const carregarBot = useCallback(async () => {
        try {
            const b = await obterBot(id);
            setBot(b);
        } catch (e) {
            toast.error("Bot não encontrado");
        } finally {
            setCarregando(false);
        }
    }, [id]);

    useEffect(() => {
        carregarBot();
    }, [carregarBot]);

    // Loop de flush: a cada animation frame atualiza o frame visível,
    // e a cada INTERVALO_FLUSH_MS faz batch de requisicoes/logs/etc.
    useEffect(() => {
        let rafId = 0;
        let ultimoFlushEventos = 0;
        let cancelado = false;

        const tick = (agora) => {
            if (cancelado) return;
            const buf = buffersRef.current;

            // Frame: força re-render apenas se chegou frame novo desde último tick.
            if (buf.frameDirty) {
                buf.frameDirty = false;
                setFrameTick((t) => t + 1);
            }

            // Eventos: flush em batch a cada INTERVALO_FLUSH_MS
            if (agora - ultimoFlushEventos >= INTERVALO_FLUSH_MS) {
                ultimoFlushEventos = agora;

                if (buf.novasReq.length > 0) {
                    const lote = buf.novasReq;
                    buf.novasReq = [];
                    // Prepend mantendo ordem cronológica recente-primeiro
                    setRequisicoes((prev) => [...lote.reverse(), ...prev].slice(0, 500));
                }
                if (buf.novosLogs.length > 0) {
                    const lote = buf.novosLogs;
                    buf.novosLogs = [];
                    setLogs((prev) => [...lote.reverse(), ...prev].slice(0, 500));
                }
                if (buf.novosEventosRegra.length > 0) {
                    const lote = buf.novosEventosRegra;
                    buf.novosEventosRegra = [];
                    setEventosRegra((prev) => [...lote.reverse(), ...prev].slice(0, 200));
                    // Sem toasts aqui — execuções em rajada poluiriam a tela.
                    // O painel "Execuções recentes" mostra status + payload + resposta.
                }
                if (buf.statusPendente) {
                    const s = buf.statusPendente;
                    buf.statusPendente = null;
                    setBot((b) => b ? { ...b, status: s.status, ultimo_erro: s.ultimo_erro } : b);
                }
            }

            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);

        return () => {
            cancelado = true;
            cancelAnimationFrame(rafId);
        };
    }, []);

    useEffect(() => {
        if (!id) return;
        const url = construirUrlWS(id);
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => setConectadoWS(true);
        ws.onclose = () => setConectadoWS(false);
        ws.onerror = () => setConectadoWS(false);
        ws.onmessage = (ev) => {
            // Hot path: roda potencialmente 100+ vezes/seg durante carga pesada.
            // Tudo aqui DEVE ser O(1) e NÃO chamar setState — apenas enfileirar no buffer.
            try {
                const msg = JSON.parse(ev.data);
                const buf = buffersRef.current;
                switch (msg.tipo) {
                    case "frame":
                        // Coalescing: só guarda o último frame. Frames antigos são descartados.
                        frameRef.current = msg.dados;
                        buf.frameDirty = true;
                        break;
                    case "requisicao_capturada":
                        buf.novasReq.push(msg.dados);
                        // Proteção contra explosão de memória se algo der errado no flush
                        if (buf.novasReq.length > 1000) buf.novasReq = buf.novasReq.slice(-500);
                        break;
                    case "log":
                        buf.novosLogs.push(msg.dados);
                        if (buf.novosLogs.length > 1000) buf.novosLogs = buf.novosLogs.slice(-500);
                        break;
                    case "regra_executada":
                        buf.novosEventosRegra.push(msg.dados);
                        if (buf.novosEventosRegra.length > 500) buf.novosEventosRegra = buf.novosEventosRegra.slice(-200);
                        break;
                    case "status":
                        buf.statusPendente = msg.dados;
                        break;
                    default:
                        break;
                }
            } catch {}
        };
        return () => {
            try { ws.close(); } catch {}
        };
    }, [id]);

    const acao = async (fn, sucesso) => {
        try {
            await fn(id);
            toast.success(sucesso);
            carregarBot();
        } catch (e) {
            toast.error(e?.response?.data?.erro || "Falha");
        }
    };

    const [confirmarExcluir, setConfirmarExcluir] = useState(false);
    const [dialogInjetarCookies, setDialogInjetarCookies] = useState(false);
    const [dialogClonar, setDialogClonar] = useState(false);
    const [nomeClonar, setNomeClonar] = useState("");
    const [clonando, setClonando] = useState(false);
    const navegar = useNavigate();

    const executarExclusao = async () => {
        try {
            await removerBot(id);
            toast.success("Bot excluído");
            window.location.href = "/";
        } catch {
            toast.error("Falha ao excluir");
        }
    };

    const abrirDialogClonar = () => {
        setNomeClonar("Cópia de " + (bot?.nome || ""));
        setDialogClonar(true);
    };

    const executarClonagem = async (e) => {
        e.preventDefault();
        if (!nomeClonar.trim()) return;
        setClonando(true);
        try {
            const resultado = await clonarBot(id, nomeClonar.trim());
            const regrasMsg = resultado.regras_clonadas > 0
                ? ` com ${resultado.regras_clonadas} regra(s)`
                : "";
            toast.success(`Bot clonado${regrasMsg}`);
            setDialogClonar(false);
            setNomeClonar("");
            navegar(`/bots/${resultado.bot.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.erro || "Falha ao clonar bot");
        } finally {
            setClonando(false);
        }
    };

    if (carregando) {
        return (
            <div className="min-h-screen bg-zinc-950 text-zinc-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 pb-20" data-testid="loading-skeleton-bot">
                    <div className="h-3 w-16 bg-zinc-800 rounded mb-3 animate-pulse"></div>
                    <div className="h-6 w-64 bg-zinc-800 rounded mb-4 animate-pulse"></div>
                    <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-zinc-900 border border-zinc-800 rounded-md animate-pulse">
                        <div className="h-5 w-20 bg-zinc-800 rounded-full"></div>
                        <div className="h-3 w-32 bg-zinc-800/70 rounded"></div>
                        <div className="h-3 w-48 bg-zinc-800/50 rounded flex-1 min-w-[120px]"></div>
                        <div className="flex items-center gap-2">
                            <div className="h-8 w-20 bg-zinc-800 rounded"></div>
                            <div className="h-8 w-8 bg-zinc-800 rounded"></div>
                            <div className="h-8 w-8 bg-zinc-800 rounded"></div>
                        </div>
                    </div>
                    <div className="h-9 w-full bg-zinc-900 border border-zinc-800 rounded animate-pulse mb-4"></div>
                    <div className="aspect-video bg-zinc-900 border border-zinc-800 rounded-md animate-pulse"></div>
                </div>
            </div>
        );
    }
    if (!bot) {
        return (
            <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center gap-3 text-zinc-400">
                <p>Bot não encontrado</p>
                <Link to="/" className="text-sm underline">Voltar</Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-zinc-950 text-zinc-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 pb-20">
                <div className="flex items-center justify-between gap-3 mb-3">
                    <Link to="/" className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1" data-testid="link-voltar">
                        <ArrowLeft size={14} /> Voltar
                    </Link>
                    <BotaoSair />
                </div>
                <h1 className="text-xl font-medium tracking-tight mb-3 truncate" data-testid="titulo-bot">{bot.nome}</h1>

                <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-zinc-900 border border-zinc-800 rounded-md">
                    <Badge variant="outline" className={`text-[10px] font-mono uppercase tracking-wider ${corStatus[bot.status] || corStatus.parado}`} data-testid="status-bot">
                        {bot.status === "rodando" && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5 pulso inline-block" />}
                        {bot.status}
                    </Badge>
                    <span className={`text-[10px] font-mono uppercase tracking-wider ${conectadoWS ? "text-emerald-400" : "text-zinc-500"}`}>
                        WS {conectadoWS ? "conectado" : "desconectado"}
                    </span>
                    <span className="text-xs text-zinc-500 truncate flex-1 min-w-[120px]">{bot.url_alvo}</span>
                    <div className="flex items-center gap-2">
                        {bot.status === "rodando" ? (
                            <Button size="sm" variant="outline" data-testid="btn-parar"
                                className="bg-transparent border-zinc-800 hover:bg-zinc-800"
                                onClick={() => acao(pararBot, "Bot parado")}>
                                <Pause size={14} className="mr-1" /> Parar
                            </Button>
                        ) : (
                            <Button size="sm" data-testid="btn-iniciar"
                                className="bg-white text-zinc-950 hover:bg-zinc-200"
                                onClick={() => acao(iniciarBot, "Bot iniciado")}>
                                <Play size={14} weight="fill" className="mr-1" /> Iniciar
                            </Button>
                        )}
                        <Button size="sm" variant="outline" data-testid="btn-reiniciar"
                            className="bg-transparent border-zinc-800 hover:bg-zinc-800"
                            onClick={() => acao(reiniciarBot, "Bot reiniciado")}>
                            <ArrowsClockwise size={14} />
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            data-testid="btn-injetar-cookies"
                            title="Injetar cookies (Get cookies.txt LOCALLY)"
                            className="bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20 text-emerald-300"
                            onClick={() => setDialogInjetarCookies(true)}
                        >
                            <Cookie size={14} weight="duotone" className="mr-1" /> Cookies
                        </Button>
                        <Button size="sm" variant="outline" data-testid="btn-clonar"
                            title="Clonar bot"
                            className="bg-cyan-500/10 border-cyan-500/30 hover:bg-cyan-500/20 text-cyan-400"
                            onClick={abrirDialogClonar}>
                            <Copy size={14} className="mr-1" /> Clonar
                        </Button>
                        <Button size="sm" variant="outline" data-testid="btn-excluir"
                            className="bg-red-500/10 border-red-500/30 hover:bg-red-500/20 text-red-400"
                            onClick={() => setConfirmarExcluir(true)}>
                            <Trash size={14} />
                        </Button>
                    </div>
                </div>

                {bot.ultimo_erro && (
                    <div className="mb-4 p-3 bg-red-500/5 border border-red-500/30 rounded-md text-xs font-mono text-red-400">
                        {bot.ultimo_erro}
                    </div>
                )}

                <Tabs value={aba} onValueChange={setAba} className="w-full">
                    <TabsList className="bg-zinc-900/50 border border-zinc-800/60 rounded-md flex overflow-x-auto hide-scrollbar w-full justify-start p-1 h-auto" data-testid="tabs-list">
                        {[
                            { v: "navegador", l: "Navegador" },
                            { v: "requisicoes", l: `Requisições${requisicoes.length ? ` (${requisicoes.length})` : ""}` },
                            { v: "cookies", l: "Cookies" },
                            { v: "regras", l: "Regras" },
                            { v: "logs", l: "Logs" },
                            { v: "config", l: "Config" },
                        ].map((t) => (
                            <TabsTrigger key={t.v} value={t.v}
                                data-testid={`tab-${t.v}`}
                                className="px-3 py-1.5 text-xs sm:text-sm font-medium rounded text-zinc-400 hover:text-zinc-100 data-[state=active]:bg-zinc-800 data-[state=active]:text-white whitespace-nowrap">
                                {t.l}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    <TabsContent value="navegador" className="mt-4">
                        <AbaNavegador botId={id} bot={bot} frameRef={frameRef} frameTick={frameTick} />
                    </TabsContent>
                    <TabsContent value="requisicoes" className="mt-4">
                        <AbaRequisicoes
                            botId={id}
                            requisicoesAoVivo={requisicoes}
                            onCriarRegraDeRequisicao={abrirRegraDeRequisicao}
                            onLimparAoVivo={() => {
                                // Zera o buffer ao vivo no pai quando o usuário limpa o histórico.
                                // Sem isso, requisições ao vivo permanecem visíveis após "limpar".
                                setRequisicoes([]);
                                buffersRef.current.novasReq = [];
                            }}
                        />
                    </TabsContent>
                    <TabsContent value="cookies" className="mt-4">
                        <AbaCookies botId={id} botStatus={bot.status} />
                    </TabsContent>
                    <TabsContent value="regras" className="mt-4">
                        <AbaRegras ref={abaRegrasRef} botId={id} eventosRegra={eventosRegra} />
                    </TabsContent>
                    <TabsContent value="logs" className="mt-4">
                        <AbaLogs botId={id} logsAoVivo={logs} />
                    </TabsContent>
                    <TabsContent value="config" className="mt-4">
                        <AbaConfig bot={bot} onAtualizado={carregarBot} />
                    </TabsContent>
                </Tabs>
            </div>

            <DialogConfirmacao
                aberto={confirmarExcluir}
                setAberto={setConfirmarExcluir}
                titulo="Excluir bot?"
                descricao={`O bot "${bot.nome}" será removido junto com toda a sessão de navegador, cookies, requisições, regras e logs. Esta ação é permanente.`}
                rotuloAcao="Excluir definitivamente"
                onConfirmar={executarExclusao}
                testId="dialog-excluir-bot"
            />

            <DialogInjetarCookies
                aberto={dialogInjetarCookies}
                setAberto={setDialogInjetarCookies}
                botId={id}
                botStatus={bot.status}
                onSucesso={carregarBot}
            />

            {/* Dialog de clonar bot */}
            <Dialog open={dialogClonar} onOpenChange={(v) => { if (!v) { setDialogClonar(false); setNomeClonar(""); } }}>
                <DialogContent className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-medium tracking-tight">Clonar Bot</DialogTitle>
                        <DialogDescription className="text-zinc-400 text-sm">
                            Criará uma cópia idêntica de &ldquo;{bot.nome}&rdquo; com todas as configurações e regras de encaminhamento.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={executarClonagem} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="nome-clone-detalhe" className="text-xs uppercase tracking-wide text-zinc-400">Nome do Novo Bot</Label>
                            <Input
                                id="nome-clone-detalhe"
                                data-testid="input-nome-clone-detalhe"
                                placeholder="ex: Coletor Site X (cópia)"
                                value={nomeClonar}
                                onChange={(e) => setNomeClonar(e.target.value)}
                                autoFocus
                                className="bg-zinc-900 border-zinc-800 focus:border-zinc-600 focus:ring-1 focus:ring-zinc-600"
                            />
                        </div>
                        <DialogFooter className="gap-2">
                            <Button type="button" variant="outline"
                                className="bg-transparent border-zinc-800 hover:bg-zinc-900"
                                onClick={() => { setDialogClonar(false); setNomeClonar(""); }}>
                                Cancelar
                            </Button>
                            <Button type="submit" disabled={clonando || !nomeClonar.trim()}
                                data-testid="confirmar-clonar-bot-detalhe"
                                className="bg-cyan-600 text-white hover:bg-cyan-700">
                                {clonando ? <CircleNotch size={16} className="animate-spin mr-2" /> : <Copy size={16} className="mr-2" />}
                                Clonar
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
